import { datadogRum } from '@datadog/browser-rum'
import { generateUUID } from './generateUUID'
import { getRiseClient } from './riseClient'

// RISE codegen modules are loaded dynamically to avoid crashing the app at startup.
// The codegen proto files are symlinks to pbgen/ outside the workspace, and static
// imports cause "Class extends value is not a constructor" errors when the bundler
// can't resolve @bufbuild/protobuf from the symlink target location.
type RiseModules = Awaited<ReturnType<typeof loadRiseModules>>
let riseModules: RiseModules | null = null
let riseModulesLoading: Promise<RiseModules | null> | null = null

async function loadRiseModules() {
  const [
    { CommonExternalProperties },
    { EventAppPageViewV1 },
    { EventAppLaunchedV1 },
    { EventNavigationActionV1 },
    { EventDashboardViewedV1 },
    { EventAuthRefreshAttemptedV1 },
    { EventAuthRefreshSuccessV1 },
  ] = await Promise.all([
    import('../../../rise-events/domains/retailer_tools/common_external'),
    import('../../../rise-events/domains/retailer_tools/event_app_dot_page_view_v1'),
    import('../../../rise-events/domains/retailer_tools/event_app_dot_launched_v1'),
    import('../../../rise-events/domains/retailer_tools/event_navigation_dot_action_v1'),
    import('../../../rise-events/domains/retailer_tools/event_dashboard_dot_viewed_v1'),
    import('../../../rise-events/domains/retailer_tools/event_auth_refresh_dot_attempted_v1'),
    import('../../../rise-events/domains/retailer_tools/event_auth_refresh_dot_success_v1'),
  ])

  return {
    CommonExternalProperties,
    eventMap: {
      'app.page_view': (data: Record<string, any>) =>
        new EventAppPageViewV1({
          url: data.url ?? window.location.href,
          isExactPathMatch: data.isExactPathMatch,
          sanitizedUrlPath: data.sanitizedUrlPath,
        }),
      'app.launched': (data: Record<string, any>) =>
        new EventAppLaunchedV1({
          sanitizedUrlPath: data.sanitizedUrlPath,
        }),
      'navV2.navigation.action': (data: Record<string, any>) =>
        new EventNavigationActionV1({
          href: data.href,
          route: data.route,
          sanitizedUrlPath: data.sanitizedUrlPath,
        }),
      'dashboard.viewed': (data: Record<string, any>) =>
        new EventDashboardViewedV1({
          sanitizedUrlPath: data.sanitizedUrlPath,
        }),
      'auth.refresh.attempted': (data: Record<string, any>) =>
        new EventAuthRefreshAttemptedV1({
          sanitizedUrlPath: data.sanitizedUrlPath,
        }),
      'auth.refresh.success': (data: Record<string, any>) =>
        new EventAuthRefreshSuccessV1({
          retryCount: data.retryCount ?? 0,
        }),
    } as Record<string, (data: Record<string, any>) => any>,
  }
}

async function getRiseModules() {
  if (riseModules) return riseModules
  if (!riseModulesLoading) {
    riseModulesLoading = loadRiseModules()
      .then(m => {
        riseModules = m
        return m
      })
      .catch(() => {
        riseModulesLoading = null
        return null
      })
  }
  return riseModulesLoading
}

function getSessionId(): string {
  try {
    const rumContext = datadogRum.getInternalContext()
    if (rumContext?.session_id) return rumContext.session_id
  } catch {
    // DD RUM may not be initialized
  }
  return generateUUID()
}

function getPageViewId(): string {
  try {
    const rumContext = datadogRum.getInternalContext()
    if (rumContext?.view?.id) return rumContext.view.id
  } catch {
    // DD RUM may not be initialized
  }
  return generateUUID()
}

export function trackRiseEvent({
  id,
  data,
}: {
  id: string
  description: string
  data?: Record<string, any>
}): void {
  try {
    const client = getRiseClient()
    if (!client) return

    const eventData = data ?? {}

    getRiseModules()
      .then(modules => {
        if (!modules) return
        const eventFactory = modules.eventMap[id]
        if (!eventFactory) return

        const event = eventFactory(eventData)
        const common = new modules.CommonExternalProperties({
          partnerId: eventData.partnerId != null ? String(eventData.partnerId) : undefined,
          retailerId: eventData.retailerId != null ? String(eventData.retailerId) : undefined,
          storeConfigId:
            eventData.storeConfigId != null ? String(eventData.storeConfigId) : undefined,
          canonicalId: eventData.canonicalId != null ? String(eventData.canonicalId) : undefined,
          isInternal: eventData.isInternal,
          sessionId: getSessionId(),
          pageViewId: getPageViewId(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        })
        client.track(event, common)
      })
      .catch(() => {
        // RISE failures must never break existing tracking
      })
  } catch {
    // RISE failures must never break existing tracking
  }
}
